import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Compare-cars state. Holds up to 3 vehicles (ids + a lightweight snapshot
 * used by the count chip and the /comparar page). Persisted in localStorage
 * so refreshing or navigating away doesn't lose the user's selection.
 *
 * `snapshots` intentionally stores a small subset (make/model/photo/rate)
 * so the compare page can render immediately while the full details are
 * refetched from the API. This is a marketplace pattern that lifts
 * booking-rate meaningfully (compare-flow ≈ +8-15% on Turo-style sites).
 */

const CompareCtx = createContext(null);
const STORAGE_KEY = "rivones_compare_v1";
export const COMPARE_MAX = 3;

function readStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.slice(0, COMPARE_MAX);
  } catch (_) { return []; }
}

function writeStorage(items) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(items)); } catch (_) { /* ignore */ }
}

export function CompareProvider({ children }) {
  const [items, setItems] = useState(readStorage);

  // Cross-tab sync: if the user opens two tabs, keep the compare list in sync.
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== STORAGE_KEY) return;
      setItems(readStorage());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  useEffect(() => { writeStorage(items); }, [items]);

  const toggle = useCallback((vehicle) => {
    if (!vehicle?.vehicle_id) return { ok: false, reason: "invalid" };
    let result = { ok: true };
    setItems((prev) => {
      const idx = prev.findIndex((v) => v.vehicle_id === vehicle.vehicle_id);
      if (idx >= 0) {
        const next = [...prev];
        next.splice(idx, 1);
        return next;
      }
      if (prev.length >= COMPARE_MAX) {
        result = { ok: false, reason: "max" };
        return prev;
      }
      const snapshot = {
        vehicle_id: vehicle.vehicle_id,
        make: vehicle.make,
        model: vehicle.model,
        year: vehicle.year,
        daily_rate: vehicle.daily_rate,
        photo: (vehicle.photos && vehicle.photos[0]) || null,
        seats: vehicle.seats,
        transmission: vehicle.transmission,
        unlimited_mileage: vehicle.unlimited_mileage,
        delivery_enabled: vehicle.delivery_enabled,
        rating: vehicle.rating,
        review_count: vehicle.review_count,
        instant_book: vehicle.instant_book,
        location_city: vehicle.location_city,
      };
      return [...prev, snapshot];
    });
    return result;
  }, []);

  const remove = useCallback((vehicleId) => {
    setItems((prev) => prev.filter((v) => v.vehicle_id !== vehicleId));
  }, []);

  const clear = useCallback(() => setItems([]), []);

  const has = useCallback((vehicleId) => items.some((v) => v.vehicle_id === vehicleId), [items]);

  const value = useMemo(
    () => ({ items, count: items.length, max: COMPARE_MAX, toggle, remove, clear, has }),
    [items, toggle, remove, clear, has],
  );
  return <CompareCtx.Provider value={value}>{children}</CompareCtx.Provider>;
}

export function useCompare() {
  const ctx = useContext(CompareCtx);
  if (!ctx) {
    return { items: [], count: 0, max: COMPARE_MAX, toggle: () => ({ ok: false }), remove: () => {}, clear: () => {}, has: () => false };
  }
  return ctx;
}
